import { FormEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useNavigationType } from "react-router-dom";
import { BezpartHeader } from "../components/BezpartHeader";
import { ApiError, apiFetch } from "../api/client";
import { AdminSummaryTab } from "../components/admin/AdminSummaryTab";
import { AdminBoardsTab } from "../components/admin/AdminBoardsTab";
import { AdminOnlineTab } from "../components/admin/AdminOnlineTab";
import { AdminInboxTab } from "../components/admin/AdminInboxTab";
import { AdminAllUsersTab } from "../components/admin/AdminAllUsersTab";
import { AdminUserSessions } from "../components/admin/AdminUserSessions";
import { AdminCopyId } from "../components/admin/AdminCopyId";
import {
  formatBytes,
  formatDate,
  formatRelative,
} from "../lib/admin/adminFormat";
import { authEntryPathForError } from "../lib/authRedirect";
import {
  AdminViewRestoreContext,
  hasAdminView,
  readAdminView,
  restoreScrollTop,
  useAdminViewState,
  writeAdminView,
} from "../lib/admin/adminViewState";

type AdminScope = "school" | "external";

type AdminUser = {
  id: string;
  email: string;
  account_kind: "school" | "external";
  created_at?: string;
  plan: { code: string; status: string; source?: string | null } | null;
};

type AdminDossier = {
  user: AdminUser;
  planPolicy: {
    planCode: string;
    maxBoards: number;
    maxFolders: number;
    allowShareLinks: boolean;
    allowMedia: boolean;
    allowCheckpoints: boolean;
    quotaBytes: number;
  };
  counts: {
    boards: number;
    folders: number;
    assets_bytes: number;
    boards_listed: number;
    assets_listed: number;
    calls_listed: number;
    visits_outgoing_listed: number;
    visits_incoming_listed: number;
  };
  boards: Array<{
    id: string;
    title: string;
    in_trash: boolean;
    deleted_at: string | null;
    created_at: string;
    updated_at: string;
  }>;
  assets: Array<{
    id: string;
    original_filename: string | null;
    mime_type: string;
    size_bytes: number;
    board_id: string | null;
    in_trash: boolean;
    created_at: string;
  }>;
  calls: Array<{
    id: string;
    board_id: string;
    external_lesson_id: string;
    occurrence_date: string;
    student_label: string | null;
    call_room_id: string;
    call_url_teacher: string;
    call_url_student: string;
    created_at: string;
  }>;
  visits_outgoing: BoardVisitEntry[];
  visits_incoming: BoardVisitEntry[];
};

type BoardVisitEntry = {
  id: string;
  board_id: string;
  board_title: string;
  visited_at: string;
  left_at: string | null;
  duration_sec: number | null;
  access_kind:
    | "session"
    | "share_link"
    | "guest_session"
    | "guest_edit"
    | "admin_spectate";
  visitor_user_id: string | null;
  visitor_email: string | null;
  visitor_label: string | null;
};

const PAGE_SIZE = 100;

/** Разделы админки. Раньше всё жило на одной странице сплошной простынёй. */
const TABS = [
  { id: "summary", label: "Сводка" },
  { id: "users", label: "Пользователи" },
  // Общий список обоих контуров — виден только тому, кто админ в обоих.
  { id: "allusers", label: "Все" },
  { id: "boards", label: "Доски" },
  { id: "online", label: "Онлайн" },
  { id: "inbox", label: "Обращения" },
] as const;
type AdminTab = (typeof TABS)[number]["id"];


const ACCESS_KIND_LABELS: Record<BoardVisitEntry["access_kind"], string> = {
  session: "Аккаунт",
  share_link: "Ссылка",
  guest_session: "Гость (ссылка)",
  guest_edit: "Гостевая доска",
  admin_spectate: "Админ",
};

function formatVisitDuration(sec: number | null, leftAt: string | null): string {
  if (!leftAt) return "на доске";
  if (sec == null || sec < 1) return "< 1 мин";
  if (sec < 60) return `${sec} сек`;
  if (sec < 3600) return `${Math.round(sec / 60)} мин`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

function visitVisitorLabel(v: BoardVisitEntry): string {
  if (v.visitor_email) return v.visitor_email;
  if (v.visitor_label) return v.visitor_label;
  return "Гость";
}

function assetContentUrl(assetId: string): string {
  return `/api/v1/assets/${encodeURIComponent(assetId)}/content`;
}

function isInlinePreviewMime(mime: string): boolean {
  return (
    mime.startsWith("image/") ||
    mime === "application/pdf" ||
    mime.startsWith("audio/") ||
    mime.startsWith("video/")
  );
}

type AssetPreview = {
  id: string;
  name: string;
  mime: string;
};

type Props = {
  scope: AdminScope;
};

export function AdminBillingPage({ scope }: Props) {
  const navigate = useNavigate();
  const isSchool = scope === "school";
  const adminPath = isSchool ? "/admin/school" : "/admin/external";
  const otherPath = isSchool ? "/admin/external" : "/admin/school";
  const otherLabel = isSchool ? "Внешняя админка" : "Школьная админка";

  // Возврат «Назад» (например, с невидимого просмотра доски) восстанавливает
  // вкладку, списки и прокрутку; обычный заход открывает админку с нуля.
  const navigationType = useNavigationType();
  const viewKey = (name: string) => `admin:${scope}:${name}`;
  const [restoredAtMount] = useState(
    () => navigationType === "POP" && hasAdminView(viewKey("tab"))
  );
  /** Сбрасывается при смене вкладки: новая вкладка грузится свежей. */
  const [restoreTabs, setRestoreTabs] = useState(restoredAtMount);
  const mainPanelRef = useRef<HTMLElement | null>(null);

  const [ready, setReady] = useAdminViewState(viewKey("ready"), false, restoredAtMount);
  const [allowed, setAllowed] = useAdminViewState(viewKey("allowed"), false, restoredAtMount);
  const [otherScopeAllowed, setOtherScopeAllowed] = useAdminViewState(
    viewKey("otherScopeAllowed"),
    false,
    restoredAtMount
  );
  const [adminEmail, setAdminEmail] = useAdminViewState(viewKey("email"), "", restoredAtMount);
  const [queryEmail, setQueryEmail] = useAdminViewState(
    viewKey("queryEmail"),
    "",
    restoredAtMount
  );
  const [listFilter, setListFilter] = useAdminViewState(
    viewKey("listFilter"),
    "",
    restoredAtMount
  );
  const [users, setUsers] = useAdminViewState<AdminUser[]>(
    viewKey("users"),
    [],
    restoredAtMount
  );
  const [totalUsers, setTotalUsers] = useAdminViewState(viewKey("totalUsers"), 0, restoredAtMount);
  const [listOffset, setListOffset] = useAdminViewState(viewKey("listOffset"), 0, restoredAtMount);
  const [listLoading, setListLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dossier, setDossier] = useAdminViewState<AdminDossier | null>(
    viewKey("dossier"),
    null,
    restoredAtMount
  );
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [promoEnabled, setPromoEnabled] = useState<boolean | null>(null);
  const [promoSaving, setPromoSaving] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<AssetPreview | null>(null);
  const [tab, setTab] = useAdminViewState<AdminTab>(viewKey("tab"), "summary", restoredAtMount);

  // Классы страницы и прокрутка — в одном layout-эффекте, потому что важен
  // порядок. Классы делают .wb-cabinet-main-panel прокручиваемым контейнером:
  // при входе ставим их ДО восстановления прокрутки, при уходе снимаем ПОСЛЕ
  // того, как запомнили позицию (без классов scrollTop сразу схлопывается в 0).
  useLayoutEffect(() => {
    document.body.classList.add("wb-page--cabinet", "wb-page--cabinet-miro");
    const el = mainPanelRef.current;
    const restore =
      el && restoredAtMount
        ? restoreScrollTop(el, readAdminView<number>(viewKey("scroll")) ?? 0)
        : null;
    return () => {
      const pending = restore?.pendingTarget() ?? null;
      restore?.stop();
      // Позицию запоминаем при уходе со страницы, пока DOM на месте: события
      // прокрутки для этого не нужны (в свёрнутом окне браузер их не шлёт).
      if (el) writeAdminView(viewKey("scroll"), pending ?? el.scrollTop);
      document.body.classList.remove("wb-page--cabinet", "wb-page--cabinet-miro");
    };
    // Только при монтировании: дальше прокруткой управляет пользователь.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = useCallback(
    async (opts: { offset: number; q: string; append: boolean }) => {
      setListLoading(true);
      setErr("");
      try {
        const params = new URLSearchParams();
        params.set("kind", scope);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(opts.offset));
        if (opts.q.trim()) params.set("q", opts.q.trim());
        const data = await apiFetch<{
          total: number;
          limit: number;
          offset: number;
          users: AdminUser[];
        }>(`/api/v1/admin/users?${params.toString()}`);
        setTotalUsers(data.total ?? 0);
        setListOffset(opts.offset);
        setUsers((prev) => (opts.append ? [...prev, ...(data.users ?? [])] : data.users ?? []));
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Не удалось загрузить пользователей");
      } finally {
        setListLoading(false);
      }
    },
    [scope]
  );

  useEffect(() => {
    // При возврате «Назад» список и карточка уже на месте — доступ перепроверяем
    // в фоне, но экран не сбрасываем, иначе потеряется прокрутка.
    if (!restoredAtMount) {
      setDossier(null);
      setUsers([]);
      setTotalUsers(0);
      setListOffset(0);
      setQueryEmail("");
      setListFilter("");
      setReady(false);
      setAllowed(false);
    }
    setErr("");
    setOk("");
    setPromoEnabled(null);

    let cancelled = false;
    void apiFetch<{
      allowed: boolean;
      email: string;
      scopes?: { school: boolean; external: boolean };
    }>("/api/v1/admin/me")
      .then((data) => {
        if (cancelled) return;
        const scopes = data.scopes ?? { school: data.allowed, external: data.allowed };
        const scopeOk = Boolean(scopes[scope]);
        setAllowed(scopeOk);
        setOtherScopeAllowed(Boolean(scopes[isSchool ? "external" : "school"]));
        setAdminEmail(data.email || "");
        setReady(true);
        if (scopeOk) {
          if (!restoredAtMount) void loadUsers({ offset: 0, q: "", append: false });
          if (!isSchool) {
            void apiFetch<{ enabled: boolean }>("/api/v1/admin/site/board-promo")
              .then((p) => {
                if (!cancelled) setPromoEnabled(Boolean(p.enabled));
              })
              .catch(() => {
                if (!cancelled) setPromoEnabled(null);
              });
          }
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          navigate(authEntryPathForError(e, adminPath), { replace: true });
          return;
        }
        setErr(e instanceof Error ? e.message : "Не удалось проверить доступ");
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, loadUsers, scope, isSchool, adminPath, restoredAtMount]);

  async function openDossier(userId: string) {
    setErr("");
    setOk("");
    setLoading(true);
    try {
      const data = await apiFetch<AdminDossier>(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/dossier?kind=${scope}`
      );
      setDossier({
        ...data,
        visits_outgoing: data.visits_outgoing ?? [],
        visits_incoming: data.visits_incoming ?? [],
        counts: {
          ...data.counts,
          visits_outgoing_listed: data.counts.visits_outgoing_listed ?? 0,
          visits_incoming_listed: data.counts.visits_incoming_listed ?? 0,
        },
      });
    } catch (e) {
      setDossier(null);
      setErr(e instanceof Error ? e.message : "Не удалось загрузить карточку");
    } finally {
      setLoading(false);
    }
  }

  async function onSearch(ev: FormEvent) {
    ev.preventDefault();
    const email = queryEmail.trim().toLowerCase();
    if (!email) return;
    setErr("");
    setOk("");
    setLoading(true);
    try {
      const data = await apiFetch<{ user: AdminUser }>(
        `/api/v1/admin/users?email=${encodeURIComponent(email)}&kind=${scope}`
      );
      if (!data.user) {
        setErr("Пользователь не найден");
        setDossier(null);
        return;
      }
      await openDossier(data.user.id);
    } catch (e) {
      setDossier(null);
      setErr(e instanceof Error ? e.message : "Ошибка поиска");
    } finally {
      setLoading(false);
    }
  }

  async function onFilterList(ev: FormEvent) {
    ev.preventDefault();
    await loadUsers({ offset: 0, q: listFilter, append: false });
  }

  async function setPlan(planCode: "free" | "pro" | "business" | "school") {
    if (!dossier?.user) return;
    setErr("");
    setOk("");
    setSaving(true);
    try {
      await apiFetch(`/api/v1/admin/users/${encodeURIComponent(dossier.user.id)}/plan`, {
        method: "PATCH",
        json: { plan_code: planCode, status: "active", kind: scope },
      });
      setOk(`Тариф обновлён: ${planCode.toUpperCase()}`);
      await openDossier(dossier.user.id);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === dossier.user.id
            ? { ...u, plan: { code: planCode, status: "active", source: "admin" } }
            : u
        )
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось обновить тариф");
    } finally {
      setSaving(false);
    }
  }

  async function toggleBoardPromo(next: boolean) {
    setErr("");
    setOk("");
    setPromoSaving(true);
    try {
      const data = await apiFetch<{ enabled: boolean }>("/api/v1/admin/site/board-promo", {
        method: "PATCH",
        json: { enabled: next },
      });
      setPromoEnabled(Boolean(data.enabled));
      setOk(data.enabled ? "Реклама школы на досках включена" : "Реклама школы на досках выключена");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось сохранить настройку рекламы");
    } finally {
      setPromoSaving(false);
    }
  }

  const user = dossier?.user ?? null;
  const canLoadMore = users.length < totalUsers;

  function openAssetPreview(asset: AdminDossier["assets"][number]) {
    const name = asset.original_filename || asset.id.slice(0, 8);
    if (asset.in_trash) {
      window.open(assetContentUrl(asset.id), "_blank", "noopener,noreferrer");
      return;
    }
    if (isInlinePreviewMime(asset.mime_type)) {
      setPreviewAsset({ id: asset.id, name, mime: asset.mime_type });
      return;
    }
    window.open(assetContentUrl(asset.id), "_blank", "noopener,noreferrer");
  }

  return (
    <div className="wb-cabinet-root">
      <BezpartHeader
        mode="logged-in"
        userName={adminEmail || "Админ"}
        logoutHref={isSchool ? "/school" : "/"}
      />
      <div className="wb-cabinet-wrap">
        <div className="wb-cabinet-layout">
          <main className="wb-cabinet-main-panel" ref={mainPanelRef}>
            <AdminViewRestoreContext.Provider value={restoreTabs}>
            {ok ? (
              <p className="wb-cabinet-toast wb-cabinet-toast--ok" role="status">
                {ok}
              </p>
            ) : null}
            {err ? (
              <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
                {err}
              </p>
            ) : null}

            <header className="wb-cabinet-panel-head">
              <div className="wb-cabinet-panel-head__titles">
                <h1 className="wb-cabinet-panel-head__title">
                  {isSchool ? "Админка школы" : "Админка внешних пользователей"}
                </h1>
                <p className="wb-cabinet-panel-head__sub">
                  {isSchool
                    ? "Только школьные аккаунты (вход через bez-part.ru): доски, уроки, тариф School."
                    : "Только внешние аккаунты: тарифы Free / Pro / Business, доски и файлы."}
                </p>
              </div>
              <div className="wb-cabinet-panel-head__folder-tools">
                {otherScopeAllowed ? (
                  <Link className="wb-board-card__action-btn wb-board-card__action-btn--ghost" to={otherPath}>
                    {otherLabel}
                  </Link>
                ) : null}
                <Link className="wb-board-card__action-btn wb-board-card__action-btn--ghost" to="/cabinet">
                  В кабинет
                </Link>
              </div>
            </header>

            <section className="wb-cabinet-panel-create" aria-label="Админ пользователей">
              {!ready ? <p className="wb-cabinet-plan-hint">Проверка доступа…</p> : null}
              {ready && !allowed ? (
                <p className="wb-cabinet-plan-hint">
                  Нет доступа к{" "}
                  {isSchool ? "школьной" : "внешней"} админке. Добавьте email в{" "}
                  {isSchool ? "`ADMIN_SCHOOL_EMAILS` или `ADMIN_ALLOWED_EMAILS`" : "`ADMIN_EXTERNAL_EMAILS` или `ADMIN_ALLOWED_EMAILS`"}{" "}
                  на API.
                  {otherScopeAllowed ? (
                    <>
                      {" "}
                      <Link to={otherPath}>{otherLabel}</Link> вам доступна.
                    </>
                  ) : null}
                </p>
              ) : null}

              {ready && allowed ? (
                <>
                  <nav className="wb-admin-tabs" aria-label="Разделы админки">
                    {TABS.filter((t) => t.id !== "allusers" || otherScopeAllowed).map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={
                          tab === t.id
                            ? "wb-admin-tabs__btn wb-admin-tabs__btn--active"
                            : "wb-admin-tabs__btn"
                        }
                        aria-current={tab === t.id ? "page" : undefined}
                        onClick={() => {
                          if (t.id === tab) return;
                          setTab(t.id);
                          setRestoreTabs(false);
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </nav>

                  {tab === "summary" ? <AdminSummaryTab kind={scope} /> : null}
                  {tab === "boards" ? <AdminBoardsTab kind={scope} /> : null}
                  {tab === "online" ? <AdminOnlineTab kind={scope} /> : null}
                  {tab === "inbox" ? <AdminInboxTab /> : null}
                  {tab === "allusers" && otherScopeAllowed ? (
                    <AdminAllUsersTab
                      onPick={(id) => {
                        setTab("users");
                        void openDossier(id);
                      }}
                    />
                  ) : null}

                  {tab === "users" ? (
                   <>
                  {!isSchool ? (
                    <div className="wb-cabinet-plan-hint wb-admin-promo-toggle">
                      <p className="wb-cabinet-plan-hint__text">
                        Реклама онлайн-школы Bezpart на досках внешних пользователей и гостей
                        (полоска под шапкой). Школьным аккаунтам не показывается.
                      </p>
                      <div className="wb-cabinet-plan-hint__actions">
                        {/* Нажимается только та кнопка, которая что-то меняет:
                            раньше обе были живыми и одинаковыми на вид, и по ним
                            нельзя было понять, включена реклама или нет. */}
                        <button
                          type="button"
                          className={
                            promoEnabled
                              ? "wb-board-card__action-btn"
                              : "wb-board-card__action-btn wb-board-card__action-btn--ghost"
                          }
                          disabled={promoSaving || promoEnabled === null || promoEnabled === true}
                          onClick={() => void toggleBoardPromo(true)}
                        >
                          Включить
                        </button>
                        <button
                          type="button"
                          className={
                            promoEnabled === false
                              ? "wb-board-card__action-btn"
                              : "wb-board-card__action-btn wb-board-card__action-btn--ghost"
                          }
                          disabled={promoSaving || promoEnabled === null || promoEnabled === false}
                          onClick={() => void toggleBoardPromo(false)}
                        >
                          Выключить
                        </button>
                        <span
                          className={
                            promoEnabled
                              ? "wb-admin-promo-state is-on"
                              : "wb-admin-promo-state"
                          }
                        >
                          {promoSaving
                            ? "сохраняем…"
                            : promoEnabled === null
                              ? "состояние неизвестно"
                              : promoEnabled
                                ? "Сейчас: показывается"
                                : "Сейчас: не показывается"}
                        </span>
                      </div>
                    </div>
                  ) : null}

                  <form className="wb-cabinet-panel-create__form" onSubmit={onSearch}>
                    <input
                      className="wb-cabinet-panel-create__input"
                      type="email"
                      required
                      placeholder="Точный email → карточка"
                      value={queryEmail}
                      onChange={(e) => setQueryEmail(e.target.value)}
                    />
                    <button
                      className="wb-cabinet-panel-create__submit"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? "Загрузка…" : "Найти"}
                    </button>
                  </form>

                  <div className="wb-admin-users">
                    <div className="wb-admin-users__head">
                      <h2 className="wb-admin-users__title">
                        {isSchool ? "Школьные пользователи" : "Внешние пользователи"}{" "}
                        <span className="wb-admin-users__count">
                          {users.length} из {totalUsers}
                        </span>
                      </h2>
                      <form className="wb-cabinet-panel-create__form" onSubmit={onFilterList}>
                        <input
                          className="wb-cabinet-panel-create__input"
                          type="search"
                          placeholder="Фильтр по email…"
                          value={listFilter}
                          onChange={(e) => setListFilter(e.target.value)}
                        />
                        <button
                          className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                          type="submit"
                          disabled={listLoading}
                        >
                          Фильтр
                        </button>
                      </form>
                    </div>

                    <div className="wb-admin-table-wrap">
                      <table className="wb-admin-table">
                        <thead>
                          <tr>
                            <th>Email</th>
                            <th>Тариф</th>
                            <th>Создан</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u) => (
                            <tr
                              key={u.id}
                              className={
                                user?.id === u.id
                                  ? "wb-admin-table__row wb-admin-table__row--active"
                                  : "wb-admin-table__row"
                              }
                              onClick={() => void openDossier(u.id)}
                            >
                              <td>{u.email}</td>
                              <td>{(u.plan?.code ?? "—").toUpperCase()}</td>
                              <td title={formatDate(u.created_at)}>
                                {formatRelative(u.created_at)}
                              </td>
                            </tr>
                          ))}
                          {!users.length && !listLoading ? (
                            <tr>
                              <td colSpan={3}>Пользователей пока нет</td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>

                    {canLoadMore ? (
                      <button
                        type="button"
                        className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                        disabled={listLoading}
                        onClick={() =>
                          void loadUsers({
                            offset: listOffset + PAGE_SIZE,
                            q: listFilter,
                            append: true,
                          })
                        }
                      >
                        {listLoading ? "Загрузка…" : "Показать ещё"}
                      </button>
                    ) : null}
                  </div>

                   </>
                  ) : null}

                  {tab === "users" && dossier && user ? (
                    <div className="wb-admin-dossier">
                      <h2 className="wb-admin-users__title">Карточка пользователя</h2>
                      <p className="wb-cabinet-plan-hint__text">
                        {user.email} · создан {formatDate(user.created_at)} · тариф{" "}
                        {(user.plan?.code ?? (isSchool ? "school" : "free")).toUpperCase()}
                        {user.plan?.source ? ` (${user.plan.source})` : ""}
                      </p>
                      <p className="wb-cabinet-plan-hint__text" style={{ marginTop: 6 }}>
                        Досок: {dossier.counts.boards} · Папок: {dossier.counts.folders} · Файлы:{" "}
                        {formatBytes(dossier.counts.assets_bytes)} · Лимит досок:{" "}
                        {Number.isFinite(dossier.planPolicy.maxBoards) &&
                        dossier.planPolicy.maxBoards >= 0
                          ? dossier.planPolicy.maxBoards
                          : "∞"}
                      </p>

                      <div className="wb-cabinet-plan-hint__actions">
                        {isSchool ? (
                          <button
                            type="button"
                            className="wb-board-card__action-btn"
                            disabled={saving}
                            onClick={() => void setPlan("school")}
                          >
                            School (без лимитов)
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                              disabled={saving}
                              onClick={() => void setPlan("free")}
                            >
                              Free
                            </button>
                            <button
                              type="button"
                              className="wb-board-card__action-btn"
                              disabled={saving}
                              onClick={() => void setPlan("pro")}
                            >
                              Pro
                            </button>
                            <button
                              type="button"
                              className="wb-board-card__action-btn"
                              disabled={saving}
                              onClick={() => void setPlan("business")}
                            >
                              Business
                            </button>
                          </>
                        )}
                      </div>

                      <h3 className="wb-admin-dossier__section">
                        Посещения пользователя ({dossier.visits_outgoing.length}
                        {dossier.counts.visits_outgoing_listed >= 200 ? ", показ до 200" : ""})
                      </h3>
                      <p className="wb-cabinet-plan-hint__text" style={{ marginBottom: 8 }}>
                        Куда заходил этот пользователь (по подключению к доске).
                      </p>
                      <div className="wb-admin-table-wrap wb-admin-dossier__scroll-block">
                        <table className="wb-admin-table">
                          <thead>
                            <tr>
                              <th>Когда</th>
                              <th>Доска</th>
                              <th>Как зашёл</th>
                              <th>Длительность</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dossier.visits_outgoing.map((v) => (
                              <tr key={v.id}>
                                <td>{formatDate(v.visited_at)}</td>
                                <td>
                                  <Link to={`/board/${v.board_id}?spectate=1`} title={v.board_title}>
                                    {v.board_title || v.board_id.slice(0, 8) + "…"}
                                  </Link>
                                </td>
                                <td>{ACCESS_KIND_LABELS[v.access_kind] ?? v.access_kind}</td>
                                <td>{formatVisitDuration(v.duration_sec, v.left_at)}</td>
                              </tr>
                            ))}
                            {!dossier.visits_outgoing.length ? (
                              <tr>
                                <td colSpan={4}>Нет записей (журнал пишется с момента обновления API)</td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>

                      <h3 className="wb-admin-dossier__section">
                        Гости на досках ({dossier.visits_incoming.length}
                        {dossier.counts.visits_incoming_listed >= 200 ? ", показ до 200" : ""})
                      </h3>
                      <p className="wb-cabinet-plan-hint__text" style={{ marginBottom: 8 }}>
                        Кто заходил на доски этого пользователя (включая гостей и админов).
                      </p>
                      <div className="wb-admin-table-wrap wb-admin-dossier__scroll-block">
                        <table className="wb-admin-table">
                          <thead>
                            <tr>
                              <th>Когда</th>
                              <th>Кто</th>
                              <th>Доска</th>
                              <th>Как зашёл</th>
                              <th>Длительность</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dossier.visits_incoming.map((v) => (
                              <tr key={v.id}>
                                <td>{formatDate(v.visited_at)}</td>
                                <td>{visitVisitorLabel(v)}</td>
                                <td>
                                  <Link to={`/board/${v.board_id}?spectate=1`} title={v.board_title}>
                                    {v.board_title || v.board_id.slice(0, 8) + "…"}
                                  </Link>
                                </td>
                                <td>{ACCESS_KIND_LABELS[v.access_kind] ?? v.access_kind}</td>
                                <td>{formatVisitDuration(v.duration_sec, v.left_at)}</td>
                              </tr>
                            ))}
                            {!dossier.visits_incoming.length ? (
                              <tr>
                                <td colSpan={5}>Нет записей (журнал пишется с момента обновления API)</td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>

                      <AdminUserSessions key={user.id} userId={user.id} />

                      <h3 className="wb-admin-dossier__section">
                        Доски ({dossier.boards.length}
                        {dossier.counts.boards_listed >= 200 ? ", показ до 200" : ""})
                      </h3>
                      <div className="wb-admin-table-wrap wb-admin-dossier__scroll-block wb-admin-dossier__scroll-block--boards">
                        <table className="wb-admin-table">
                          <thead>
                            <tr>
                              <th>Название</th>
                              <th>Статус</th>
                              <th>Обновлена</th>
                              <th>ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dossier.boards.map((b) => (
                              <tr key={b.id}>
                                <td>
                                  {b.in_trash ? (
                                    <span>{b.title || "Без названия"}</span>
                                  ) : (
                                    <Link
                                      to={`/board/${b.id}?spectate=1`}
                                      title="Невидимый вход: только смотреть, участники не видят"
                                    >
                                      {b.title || "Без названия"}
                                    </Link>
                                  )}
                                </td>
                                <td>{b.in_trash ? "Корзина" : "Активна"}</td>
                                <td>{formatDate(b.updated_at)}</td>
                                <td>
                                  <AdminCopyId id={b.id} />
                                </td>
                              </tr>
                            ))}
                            {!dossier.boards.length ? (
                              <tr>
                                <td colSpan={4}>Нет досок</td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>

                      <h3 className="wb-admin-dossier__section">
                        Файлы ({dossier.assets.length}
                        {dossier.counts.assets_listed >= 200 ? ", показ до 200" : ""})
                      </h3>
                      <div className="wb-admin-table-wrap wb-admin-dossier__scroll-block">
                        <table className="wb-admin-table">
                          <thead>
                            <tr>
                              <th>Имя</th>
                              <th>Тип</th>
                              <th>Размер</th>
                              <th>Доска</th>
                              <th>Создан</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dossier.assets.map((a) => (
                              <tr key={a.id}>
                                <td>
                                  <button
                                    type="button"
                                    className="wb-admin-asset-link"
                                    title="Посмотреть файл"
                                    onClick={() => openAssetPreview(a)}
                                  >
                                    {a.original_filename || a.id.slice(0, 8)}
                                    {a.in_trash ? " (удалён)" : ""}
                                  </button>
                                </td>
                                <td>{a.mime_type}</td>
                                <td>{formatBytes(a.size_bytes)}</td>
                                <td className="wb-admin-table__mono">
                                  {a.board_id ? `${a.board_id.slice(0, 8)}…` : "—"}
                                </td>
                                <td>{formatDate(a.created_at)}</td>
                              </tr>
                            ))}
                            {!dossier.assets.length ? (
                              <tr>
                                <td colSpan={5}>Нет файлов</td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>

                      {isSchool ? (
                        <>
                          <h3 className="wb-admin-dossier__section">
                            Созвоны / уроки ({dossier.calls.length}
                            {dossier.counts.calls_listed >= 200 ? ", показ до 200" : ""})
                          </h3>
                          <p className="wb-cabinet-plan-hint__text" style={{ marginBottom: 8 }}>
                            Сессии из интеграции уроков (`lesson_board_sessions`).
                          </p>
                          <div className="wb-admin-table-wrap">
                            <table className="wb-admin-table">
                              <thead>
                                <tr>
                                  <th>Дата</th>
                                  <th>Ученик</th>
                                  <th>Lesson</th>
                                  <th>Комната</th>
                                  <th>Доска</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dossier.calls.map((c) => (
                                  <tr key={c.id}>
                                    <td>{c.occurrence_date}</td>
                                    <td>{c.student_label || "—"}</td>
                                    <td className="wb-admin-table__mono">{c.external_lesson_id}</td>
                                    <td className="wb-admin-table__mono">{c.call_room_id}</td>
                                    <td>
                                      <Link
                                        to={`/board/${c.board_id}?spectate=1`}
                                        title="Невидимый просмотр урока"
                                      >
                                        {c.board_id.slice(0, 8)}…
                                      </Link>
                                    </td>
                                  </tr>
                                ))}
                                {!dossier.calls.length ? (
                                  <tr>
                                    <td colSpan={5}>Нет сессий звонков</td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : null}
            </section>
            </AdminViewRestoreContext.Provider>
          </main>
        </div>
      </div>

      {previewAsset ? (
        <div
          className="wb-admin-asset-preview"
          role="dialog"
          aria-modal="true"
          aria-label={previewAsset.name}
          onClick={() => setPreviewAsset(null)}
        >
          <div
            className="wb-admin-asset-preview__panel"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="wb-admin-asset-preview__head">
              <span className="wb-admin-asset-preview__title">{previewAsset.name}</span>
              <button
                type="button"
                className="wb-admin-asset-preview__close"
                aria-label="Закрыть"
                onClick={() => setPreviewAsset(null)}
              >
                ×
              </button>
            </header>
            <div className="wb-admin-asset-preview__body">
              {previewAsset.mime.startsWith("image/") ? (
                <img
                  src={assetContentUrl(previewAsset.id)}
                  alt={previewAsset.name}
                  className="wb-admin-asset-preview__img"
                />
              ) : null}
              {previewAsset.mime === "application/pdf" ? (
                <iframe
                  title={previewAsset.name}
                  src={assetContentUrl(previewAsset.id)}
                  className="wb-admin-asset-preview__frame"
                />
              ) : null}
              {previewAsset.mime.startsWith("audio/") ? (
                <audio
                  controls
                  src={assetContentUrl(previewAsset.id)}
                  className="wb-admin-asset-preview__audio"
                />
              ) : null}
              {previewAsset.mime.startsWith("video/") ? (
                <video
                  controls
                  src={assetContentUrl(previewAsset.id)}
                  className="wb-admin-asset-preview__video"
                />
              ) : null}
            </div>
            <footer className="wb-admin-asset-preview__foot">
              <a
                href={assetContentUrl(previewAsset.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Открыть в новой вкладке
              </a>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Старый URL → внешняя админка. */
export function AdminBillingRedirect() {
  return <Navigate to="/admin/external" replace />;
}
